import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, ThemeProvider, Typography,
} from '@material-ui/core';

import { useLazyQuery } from '@apollo/client';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';
import { useAppSuppliedRedirectTo } from '../hooks/usePostAuthRedirectTo';

import sleepioTheme from '@/themes/sleepioTheme';
import EmailAuthForm from '@/pages/Onboarding/components/EmailAuthForm/EmailAuthForm';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { useConsent } from '@/hooks/useConsent';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { activateEmail, loginUser } from '@/utils/sparkActivationApi';
import { getLob } from '@/utils/productHelpers';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { linkPostHogIdentity } from '@/utils/posthog';

const EmailEntryInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();

  const { t } = useTranslation();
  const history = useHistory();
  const base = useActivationBase();
  const { state, dispatch } = useActivation();
  const isCreate = state.accountIntent === AccountIntent.Create;
  const redirectTo = useAppSuppliedRedirectTo();

  const posthog = usePostHog();
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, { fetchPolicy: 'network-only' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch({ type: 'SET_STEP', step: 'email_entry' });
    dispatch({ type: 'SET_AUTH_METHOD', authMethod: 'email' });
  }, [dispatch]);

  const handleSubmit = async (email: string) => {
    setLoading(true);
    // Nothing reads this intent. It was written for the callback, but the magic link opens
    // a fresh tab and sessionStorage is per-tab, so it is always absent there — which is
    // why the callback derives intent from the token instead. Removed rather than left as
    // a false lead (PAT-1281).
    try {
      if (isCreate) {
        // Sign-up: enroll the new patient and send the magic link.
        await activateEmail({
          email,
          product: 'sleepio',
          organizationSlug: state.orgSlug,
          lob: getLob(state.orgSlug),
          redirectTo,
          phDistinctId: posthog?.get_distinct_id() || undefined,
        });
      } else {
        // Returning-user login: send the magic link WITHOUT re-enrolling.
        // /activate/email re-runs Cortex enrollment, which fails for an
        // already-enrolled patient ("Patient enrollment failed"); /api/auth/login
        // sends the link with shouldCreateUser: false and no enrollment.
        // (Mirrors SparkLogin/LoginEmail.)
        await loginUser({
          email,
          product: 'sleepio',
          // Relay the address the app asked for, so it survives the email. The auth
          // service hangs it on the callback URL in the magic link; without it the
          // callback opens in a fresh tab with nothing to go on.
          redirectTo,
          ph_distinct_id: posthog?.get_distinct_id() || undefined,
        });
      }

      // Non-blocking: don't delay /sent navigation for the analytics lookup.
      linkPostHogIdentity(getDistinctId, posthog, email, '[EmailEntry]');

      dispatch({ type: 'SET_EMAIL', email });
      dispatch({ type: 'SET_STEP', step: 'magic_link_sent' });
      history.push(`${base}/sent`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      <SleepioHeader />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <Box style={{ marginTop: 32, marginBottom: 40 }}>
          <Typography variant="h2" color="textPrimary" style={{ marginBottom: 8 }}>
            {t('coverage.pre_onboarding.sleepio.activation.email_entry.title')}
          </Typography>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.email_entry.sub_title')}
          </Typography>
        </Box>
        <EmailAuthForm
          onSubmit={handleSubmit}
          loading={loading}
          showConsent={isCreate}
          i18nKeys={{
            continueButton: 'coverage.pre_onboarding.sleepio.activation.email_entry.continue',
            submittingButton: 'coverage.pre_onboarding.sleepio.activation.sending',
            errorMessage: 'coverage.pre_onboarding.sleepio.activation.error_send_failed',
          }}
        />
        <ConsentWidget />
      </Container>
      <Footer />
    </Box>
  );
};

export const EmailEntry: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <EmailEntryInner />
  </ThemeProvider>
);
